import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "../components/Header";
import ContactFooter from "../components/ContactFooter";
import { SUPPORT_EMAIL, ENTITY_NAME } from "../data/siteContent";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-parchment min-h-screen antialiased">
      <Header light />
      <article className="pt-36 sm:pt-44 pb-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-goldeep text-sm font-medium tracking-wide hover:text-ink transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>

          <h1 className="font-display text-3xl sm:text-4xl text-ink tracking-tight leading-[1.1] mt-8">
            Privacy <em className="text-goldeep italic">Policy</em>
          </h1>
          <p className="text-ink/50 text-sm mt-4 tracking-wide">
            Effective date: June 2026 &middot; Iron Gate Consulting, operated by {ENTITY_NAME}
          </p>
          <p className="text-ink/70 text-base leading-relaxed mt-8">
            All personal information you provide, including Social Security
            Numbers, is protected with military grade AES 256 GCM encryption.
          </p>

          <div className="mt-10 space-y-8 text-ink/75 text-base leading-relaxed">
            <section>
              <h2 className="font-display text-xl text-ink mb-3">1. Information We Collect</h2>
              <p>
                {ENTITY_NAME}, operating as Iron Gate Consulting, collects information you
                voluntarily provide when you contact us, complete an intake through our portal
                at trust.igchq.com, submit our contact form, or request a review of an existing
                trust. This may include your name, email address, phone number, mailing address,
                intake form responses, information about your assets and beneficiaries, and,
                where required for trust preparation, government identifiers such as Social
                Security Numbers.
              </p>
              <p className="mt-4">
                We also collect limited technical information automatically
                when you visit this website, including device type, browser
                type, pages viewed, and general location, through cookies and
                analytics tools such as the Facebook pixel and similar
                tracking technologies.
              </p>
            </section>
            <section>
              <h2 className="font-display text-xl text-ink mb-3">2. How We Use Your Information</h2>
              <p>
                We use your information to prepare and deliver the services
                you request, including living trust setup, asset protection
                structuring, and legal review coordination; to respond to contact form
                submissions and questions; to communicate
                with you about your file, including the personal call you
                receive before anything finalizes; to improve our website and
                services; and to comply with legal and regulatory
                obligations.
              </p>
              <p className="mt-4">
                <strong className="text-ink">
                  We do not sell your personal information to third parties, ever.
                </strong>{" "}
                We do not share your information with third parties for their own marketing
                purposes.
              </p>
            </section>
            <section>
              <h2 className="font-display text-xl text-ink mb-3">3. How We Store &amp; Protect Your Information</h2>
              <p>
                All personal information you submit, including Social
                Security Numbers and financial details, is protected with
                military grade AES 256 GCM encryption in transit and at rest.
                Contact form submissions are stored securely in our database and are
                accessible only to authorized personnel. Access to client files is restricted
                to personnel directly involved in preparing your documents and to the licensed
                attorneys performing legal review.
              </p>
              <p className="mt-4">
                While no method of transmission or storage is completely
                secure, we maintain administrative, technical, and physical
                safeguards designed to protect your information against
                unauthorized access, disclosure, alteration, or destruction.
              </p>
            </section>
            <section>
              <h2 className="font-display text-xl text-ink mb-3">4. Sharing of Information</h2>
              <p>
                We share your information only as needed to deliver your
                services: with licensed attorneys performing legal review of
                your file, with service providers who help operate our portal
                and communications under confidentiality obligations, and
                where required by law, court order, or to protect our legal
                rights. {ENTITY_NAME} never sells client data.
              </p>
            </section>
            <section>
              <h2 className="font-display text-xl text-ink mb-3">5. Your Rights</h2>
              <p>
                You have the right to access the personal information we hold
                about you, request that it be corrected or updated, request
                deletion of your information where it is no longer needed for
                your file or required by law, and opt out of marketing
                communications at any time.
              </p>
              <p className="mt-4">
                To exercise any of these rights or ask any privacy question, contact us at{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-goldeep underline">
                  {SUPPORT_EMAIL}
                </a>
                . We will respond to verified requests within a reasonable
                timeframe.
              </p>
            </section>
            <section>
              <h2 className="font-display text-xl text-ink mb-3">6. Cookies and Tracking</h2>
              <p>
                This website uses cookies and pixels, including the Facebook
                pixel, to measure site performance and advertising
                effectiveness. You can control cookies through your browser
                settings and opt out of interest-based advertising through the
                tools provided by your browser or the relevant advertising
                platforms.
              </p>
            </section>
            <section>
              <h2 className="font-display text-xl text-ink mb-3">7. Children's Privacy</h2>
              <p>
                Our services are intended for adults. We do not knowingly
                collect personal information from anyone under the age of 18.
                Information about minor beneficiaries is collected only as
                provided by a parent or legal guardian for trust preparation
                purposes.
              </p>
            </section>
            <section>
              <h2 className="font-display text-xl text-ink mb-3">8. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Any
                changes will be posted on this page with an updated effective
                date. Your continued use of this website after changes are
                posted constitutes acceptance of the revised policy.
              </p>
            </section>
            <section>
              <h2 className="font-display text-xl text-ink mb-3">9. Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy or how your
                information is handled, contact {ENTITY_NAME} at{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-goldeep underline">
                  {SUPPORT_EMAIL}
                </a>{" "}
                or through our{" "}
                <Link to="/contact" className="text-goldeep underline">
                  Contact page
                </Link>
                .
              </p>
            </section>
            <p className="text-ink/50 text-sm italic pt-6 border-t border-ink/10">
              Iron Gate Consulting is not a law firm and is operated by {ENTITY_NAME}. Legal
              review services are provided through veteran military benefits. Trust services are
              provided for estate planning purposes. Consult a licensed
              attorney for legal advice.
            </p>
          </div>
        </div>
      </article>
      <ContactFooter />
    </div>
  );
};

export default PrivacyPolicy;
